import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { Icon, Div } from '@pinpoint/design-kit';
import { Ocean, Segments, Datasets, styles } from '@pinpoint/onpoint-charts';

import Button from 'src/components/button';
import CONSTANTS from 'src/store/constants';

import Message from './message';
import Fullscreen from './fullscreen';

/* eslint-disable react/prop-types */

function Slide({ slide, segments: allSegments }) {
  const { number, view, segments, presignedUrl } = slide;
  switch (view) {
    case CONSTANTS.VIEWS.IMAGE.code:
      return <img src={presignedUrl} alt="Unable to load content" />;
    case CONSTANTS.VIEWS.SUMMARY.code:
      return <Datasets key={number} segments={allSegments} style={styles.Style1} minH="100%" minW="100%" />;
    case CONSTANTS.VIEWS.MARKETING_TAKEAWAYS.code:
      return (
        <Ocean
          key={number}
          objects={segments.slice(0, 1)}
          style={styles.Style1}
          minH="100%"
          minW="100%"
          personalityExplorerMode={false}
        />
      );
    case CONSTANTS.VIEWS.PERSONALITY_EXPLORER.code:
      return (
        <Ocean
          key={number}
          objects={segments.slice(0, 1)}
          style={styles.Style1}
          minH="100%"
          minW="100%"
          personalityExplorerMode
        />
      );
    case CONSTANTS.VIEWS.FACETS_VIEW.code:
      return <Segments key={number} objects={segments.slice(0, 1)} style={styles.Style1} minH="100%" minW="100%" />;
    case CONSTANTS.VIEWS.FACETS_COMPARISON.code:
      return <Segments key={number} objects={segments} style={styles.Style1} minH="100%" minW="100%" />;
    default:
      return <Message type="error" text="Unknown Visualization" />;
  }
}

/* eslint-enable react/prop-types */

function Presentation({
  story,
  segments,
  hasData,
  apiStatus,
  currentSlide,
  hasNextSlide,
  hasPrevSlide,
  onNext,
  onPrev,
  onClose,
}) {
  const [isFullScreen, setFullScreenMode] = useState(false);
  return (
    <Fullscreen
      enabled={isFullScreen}
      onChange={(mode) => {
        setFullScreenMode(mode);
      }}
    >
      <Div
        pos="fixed"
        top="0"
        left="0"
        minW="100vw"
        minH="100vh"
        w="100%"
        h="100%"
        bg="secondary"
        p={{ t: '2.5rem', x: '0.4rem' }}
        zIndex="100"
        overflow="scroll"
      >
        {hasData ? (
          <>
            <Div d="flex" pos="absolute" top="0.4rem" left="0.4rem" m={{ x: '-0.5rem' }}>
              <Button
                buttonStyle="primary"
                onClick={() => {
                  onClose();
                }}
                data-tip="close"
              >
                <Icon color="white" name="Cross" />
              </Button>
              <Button
                buttonStyle="primary"
                disabled={!hasPrevSlide}
                onClick={() => {
                  onPrev();
                }}
                data-tip="previous"
              >
                <Icon color="white" name="LeftArrow" />
              </Button>
              <Button
                buttonStyle="primary"
                disabled={!hasNextSlide}
                onClick={() => {
                  onNext();
                }}
                data-tip="next"
              >
                <Icon color="white" name="RightArrow" />
              </Button>
              <Button
                buttonStyle="primary"
                m={{ r: '0rem' }}
                onClick={() => {
                  setFullScreenMode(!isFullScreen);
                }}
                data-tip="toggle fullscreen"
              >
                <Icon color="white" name="Expand" />
              </Button>
            </Div>
            <Slide slide={story.slides[currentSlide]} segments={segments} />
          </>
        ) : apiStatus === CONSTANTS.API.STARTED || apiStatus === undefined ? (
          <Message />
        ) : apiStatus === CONSTANTS.API.FAILED ? (
          <Message type="error" />
        ) : apiStatus === CONSTANTS.API.SUCEEDED ? (
          <Message type="info" text="No data" />
        ) : (
          <Message />
        )}
      </Div>
    </Fullscreen>
  );
}
Presentation.propTypes = {
  story: PropTypes.shape({
    slides: PropTypes.arrayOf(
      PropTypes.shape({
        view: PropTypes.string,
        segments: PropTypes.shape({}),
      })
    ),
  }).isRequired,
  segments: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  hasData: PropTypes.bool.isRequired,
  apiStatus: PropTypes.oneOf([CONSTANTS.API.STARTED, CONSTANTS.API.SUCCEEDED, CONSTANTS.API.FAILED, undefined])
    .isRequired,
  currentSlide: PropTypes.number.isRequired,
  hasNextSlide: PropTypes.func.isRequired,
  hasPrevSlide: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  onPrev: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default Presentation;
